import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button, Dialog, Input } from '@/components/ui';
import { api } from '@/lib/api';

/** Global quick capture: press `c` (outside inputs) or use the + button. Creates an idea or task without a parent. */
export function QuickCapture() {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [type, setType] = useState<'idea' | 'task'>('task');
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      const typing = el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName);
      if (!typing && e.key === 'c' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        setOpen(true);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('gb:capture', onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('gb:capture', onOpen);
    };
  }, []);
  const create = useMutation({
    mutationFn: () => api.createNode({ type, title: title.trim() }),
    onSuccess: (n) => {
      toast.success(`Captured to inbox: ${n.title}`);
      setTitle('');
      setOpen(false);
    },
  });
  return (
    <Dialog open={open} onOpenChange={setOpen} title="Quick capture">
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (title.trim()) create.mutate();
        }}
      >
        <Input
          autoFocus
          placeholder="What's on your mind?"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <div className="flex items-center justify-between">
          <div className="flex gap-3 text-sm">
            {(['task', 'idea'] as const).map((t) => (
              <label key={t} className="flex items-center gap-1">
                <input
                  type="radio"
                  className="accent-[var(--color-primary)]"
                  checked={type === t}
                  onChange={() => setType(t)}
                />{' '}
                {t}
              </label>
            ))}
          </div>
          <Button type="submit" disabled={!title.trim() || create.isPending}>
            Add to inbox
          </Button>
        </div>
        <p className="text-xs text-muted-fg">Shortcut: c · search and jump: ⌘K</p>
      </form>
    </Dialog>
  );
}
